import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import { Alert, Badge, Button, Card, CardHeader, Field, Input } from '@truecare/ui';
import type { FacilityRolePreset, FacilityUserRow } from '@truecare/schemas';
import { api } from '../api';

/**
 * Facility Admin self-service user management (`ACCOUNT_MANAGE`-gated tab).
 *
 * A facility Admin invites + manages their own team here — no operator involved.
 * The backend enforces the two invariants (first-user-Admin, never-zero-Admins);
 * a "last Admin" attempt comes back as a 400 whose message we surface inline.
 */

const PRESETS: { value: FacilityRolePreset; label: string }[] = [
  { value: 'COORDINATOR', label: 'Coordinator — post & manage shifts (no rates or invoices)' },
  { value: 'APPROVER', label: 'Approver — sign timesheets & rate (sees rates)' },
  { value: 'PAYER', label: 'Payer — invoices & rates' },
  { value: 'ADMIN', label: 'Admin — full access (incl. managing users)' },
];

function roleText(preset: FacilityRolePreset | null, capCount: number): string {
  if (preset) return preset.charAt(0) + preset.slice(1).toLowerCase();
  return `Custom (${capCount})`;
}

export function FacilityUsersTab() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['me', 'facility', 'users'] });

  const usersQuery = useQuery({
    queryKey: ['me', 'facility', 'users'],
    queryFn: () => unwrap(api.GET('/api/v1/me/facility/users')),
  });

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [invitePreset, setInvitePreset] = useState<FacilityRolePreset>('COORDINATOR');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: (body: { email: string; displayName: string; preset: FacilityRolePreset }) =>
      unwrap(api.POST('/api/v1/me/facility/users', { body })),
    onSuccess: () => {
      setEmail('');
      setDisplayName('');
      setInvitePreset('COORDINATOR');
      setInviteError(null);
      invalidate();
    },
    onError: (e: unknown) => setInviteError(messageOf(e) ?? 'Invite failed.'),
  });

  const updateRole = useMutation({
    mutationFn: (v: { userId: string; preset: FacilityRolePreset }) =>
      unwrap(
        api.PATCH('/api/v1/me/facility/users/{userId}', {
          params: { path: { userId: v.userId } },
          body: { preset: v.preset },
        }),
      ),
    onSuccess: () => {
      setRowError(null);
      invalidate();
    },
    onError: (e: unknown) => setRowError(messageOf(e) ?? 'Role change failed.'),
  });

  const remove = useMutation({
    mutationFn: (userId: string) =>
      unwrap(api.DELETE('/api/v1/me/facility/users/{userId}', { params: { path: { userId } } })),
    onSuccess: () => {
      setRowError(null);
      invalidate();
    },
    onError: (e: unknown) => setRowError(messageOf(e) ?? 'Remove failed.'),
  });

  return (
    <Card padding="md" data-testid="facility-users-tab">
      <CardHeader
        title="Users & access"
        description="Invite the people who manage this facility and set what each can do. A facility must always keep at least one Admin."
      />

      {usersQuery.isPending ? (
        <p className="font-body text-sm text-ink-muted">Loading users…</p>
      ) : usersQuery.isError ? (
        <Alert tone="danger" title="Couldn't load users">
          Refresh the page.
        </Alert>
      ) : (
        <>
          {rowError && (
            <Alert tone="danger" className="mb-3" data-testid="facility-users-row-error">
              {rowError}
            </Alert>
          )}

          <ul className="mb-6 divide-y divide-border">
            {usersQuery.data.users.map((u: FacilityUserRow) => (
              <li
                key={u.userId}
                className="flex flex-wrap items-center justify-between gap-3 py-3"
                data-testid="facility-users-row"
              >
                <div className="min-w-0">
                  <p className="font-heading text-sm text-ink">
                    {u.displayName ?? u.email}
                    {u.isSelf && <span className="ml-1 text-xs text-ink-muted">(you)</span>}
                    {u.pending && (
                      <span className="ml-2 align-middle">
                        <Badge tone="neutral" variant="soft">
                          Invited
                        </Badge>
                      </span>
                    )}
                  </p>
                  <p className="font-body text-xs text-ink-muted">{u.email}</p>
                  <p className="mt-0.5 font-body text-xs text-ink-muted">
                    Role:{' '}
                    <span className="font-medium text-ink">
                      {roleText(u.preset, u.capabilities.length)}
                    </span>
                  </p>
                </div>
                {u.isSelf ? (
                  // No self-management: an Admin can't remove or demote their own
                  // access here (the backend blocks the last-Admin case, but with
                  // 2+ Admins self-removal/demotion would silently drop you out of
                  // this tab). Another Admin changes your access.
                  <span
                    className="font-body text-xs text-ink-muted"
                    data-testid={`facility-users-self-note-${u.userId}`}
                  >
                    You can’t change your own access
                  </span>
                ) : (
                  <div className="flex items-center gap-2">
                    <label className="sr-only" htmlFor={`role-${u.userId}`}>
                      Change role for {u.displayName ?? u.email}
                    </label>
                  <select
                    id={`role-${u.userId}`}
                    className="rounded-md border border-border bg-paper p-1.5 text-xs"
                    value={u.preset ?? ''}
                    disabled={updateRole.isPending}
                    onChange={(e) => {
                      const preset = e.target.value as FacilityRolePreset;
                      if (!preset) return;
                      const label = PRESETS.find((p) => p.value === preset)?.label ?? preset;
                      if (
                        typeof window !== 'undefined' &&
                        !window.confirm(`Change ${u.displayName ?? u.email}’s role to ${label}?`)
                      ) {
                        e.target.value = u.preset ?? '';
                        return;
                      }
                      updateRole.mutate({ userId: u.userId, preset });
                    }}
                    data-testid={`facility-users-role-${u.userId}`}
                  >
                    {!u.preset && (
                      <option value="" disabled>
                        Custom — pick a role…
                      </option>
                    )}
                    {PRESETS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="ghost"
                    size="sm"
                    loading={remove.isPending}
                    // a11y (WCAG 2.5.3) — the visible text "Remove" repeats on every
                    // row; give each an item-specific accessible name so a screen
                    // reader announces WHO is being removed, matching the sr-only
                    // label on the role <select> in the same row.
                    aria-label={`Remove ${u.displayName ?? u.email} from this facility`}
                    onClick={() => {
                      if (
                        typeof window !== 'undefined' &&
                        window.confirm(`Remove ${u.displayName ?? u.email} from this facility?`)
                      ) {
                        remove.mutate(u.userId);
                      }
                    }}
                    data-testid={`facility-users-remove-${u.userId}`}
                  >
                    Remove
                  </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>

          <form
            className="flex flex-col gap-3 border-t border-border pt-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim().length === 0 || displayName.trim().length === 0) return;
              invite.mutate({
                email: email.trim().toLowerCase(),
                displayName: displayName.trim(),
                preset: invitePreset,
              });
            }}
          >
            <p className="font-heading text-sm text-ink">Invite a user</p>
            {inviteError && (
              <Alert tone="danger" data-testid="facility-users-invite-error">
                {inviteError}
              </Alert>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Email">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@facility.example"
                  data-testid="facility-users-invite-email"
                />
              </Field>
              <Field label="Name">
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Jane Doe"
                  data-testid="facility-users-invite-name"
                />
              </Field>
            </div>
            <Field label="Role">
              <select
                className="w-full rounded-md border border-border bg-paper p-2 text-sm"
                value={invitePreset}
                onChange={(e) => setInvitePreset(e.target.value as FacilityRolePreset)}
                data-testid="facility-users-invite-preset"
              >
                {PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>
            <div>
              <Button
                type="submit"
                size="sm"
                variant="primary"
                loading={invite.isPending}
                disabled={email.trim().length === 0 || displayName.trim().length === 0}
                data-testid="facility-users-invite-button"
              >
                Send invite
              </Button>
            </div>
          </form>
        </>
      )}
    </Card>
  );
}
